import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../api/client";
import useFilterParams from "../../hooks/useFilterParams";
import type { EmployeeSummary, Site } from "../../types";
import { formatRole } from "../../utils/formatters";
import DropdownSelect from "../../components/DropdownSelect";
import { Check, Mail } from "lucide-react";

const TIME_FMT = new Intl.DateTimeFormat(undefined, {
  dateStyle: "short",
  timeStyle: "short",
});

interface WelcomeStatus {
  employee_id: number;
  welcome_sent: boolean;
  welcome_sent_at: string | null;
  has_logged_in: boolean;
}

interface AlreadySentEntry {
  employee_id: number;
  email: string;
  name: string;
  sent_at: string | null;
}

interface SendWelcomeResponse {
  sent: number;
  failed: number;
  already_sent: AlreadySentEntry[];
  results: { employee_id: number; email: string; success: boolean; error: string | null }[];
  email_paused?: boolean;
}

const WELCOME_PAUSED_NOTE = "Email is paused. No welcome emails were sent and no PINs were changed.";

const EMPLOYEE_FILTERS = { search: "", site: "", role: "" };

export default function EmployeeList() {
  const [rows, setRows] = useState<EmployeeSummary[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [filters, setFilter] = useFilterParams(EMPLOYEE_FILTERS);
  const [error, setError] = useState<string | null>(null);
  const [welcomeMap, setWelcomeMap] = useState<Record<number, WelcomeStatus>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult] = useState<string | null>(null);
  const [bulkPaused, setBulkPaused] = useState(false);
  const [resendConfirm, setResendConfirm] = useState<AlreadySentEntry[] | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const [employees, siteList, wStatus] = await Promise.all([
          api.get<EmployeeSummary[]>("/api/employees"),
          api.get<Site[]>("/api/sites"),
          api.get<WelcomeStatus[]>("/api/employees/welcome-status"),
        ]);
        setRows(employees);
        setSites(siteList);
        const wMap: Record<number, WelcomeStatus> = {};
        for (const w of wStatus) wMap[w.employee_id] = w;
        setWelcomeMap(wMap);
      } catch (err) {
        setError((err as Error).message);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filters.site && String(r.site_id) !== filters.site) return false;
      if (filters.role && r.role !== filters.role) return false;
      if (!q) return true;
      return (
        r.first_name.toLowerCase().includes(q) ||
        r.last_name.toLowerCase().includes(q) ||
        r.email.toLowerCase().includes(q) ||
        r.employee_number.toLowerCase().includes(q)
      );
    });
  }, [rows, filters.search, filters.site, filters.role]);

  const siteName = (id: number) =>
    sites.find((s) => s.id === id)?.name ?? `Site #${id}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Employees</h1>
          <p className="text-sm text-gray-500">{rows.length} total</p>
        </div>
        <Link
          to="/admin/employees/new"
          className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white"
        >
          + Add Employee
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          type="search"
          placeholder="Search name, email, or employee #"
          value={filters.search}
          onChange={(e) => setFilter("search", e.target.value)}
          className="w-72 rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <select
          value={filters.site}
          onChange={(e) => setFilter("site", e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">All sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <DropdownSelect
          listCode="role"
          value={filters.role}
          onChange={(v: string) => setFilter("role", v)}
          placeholder="All roles"
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-pme-blue/30 bg-pme-blue/5 px-4 py-2 text-sm">
          <span className="text-gray-700">{selected.size} selected</span>
          <button
            type="button"
            disabled={bulkSending}
            onClick={async () => {
              if (!confirm(`Send welcome emails to ${selected.size} employees? This will generate new PINs.`)) return;
              setBulkSending(true);
              setBulkResult(null);
              try {
                const res = await api.post<SendWelcomeResponse>("/api/employees/send-welcome-emails", {
                  employee_ids: Array.from(selected),
                });
                const parts: string[] = [];
                if (res.sent > 0) parts.push(`${res.sent} sent`);
                if (res.failed > 0) parts.push(`${res.failed} failed`);
                if (res.already_sent?.length > 0) parts.push(`${res.already_sent.length} already sent`);
                setBulkPaused(!!res.email_paused);
                setBulkResult(res.email_paused ? WELCOME_PAUSED_NOTE : parts.join(", ") || "No emails to send");
                if (res.already_sent?.length > 0) {
                  setResendConfirm(res.already_sent);
                } else {
                  setSelected(new Set());
                }
                const wStatus = await api.get<WelcomeStatus[]>("/api/employees/welcome-status");
                const wMap: Record<number, WelcomeStatus> = {};
                for (const w of wStatus) wMap[w.employee_id] = w;
                setWelcomeMap(wMap);
              } catch {
                setBulkResult("Failed to send");
              } finally {
                setBulkSending(false);
              }
            }}
            className="rounded-lg bg-pme-blue px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
          >
            {bulkSending ? "Sending..." : `Send Welcome Emails (${selected.size})`}
          </button>
          <button type="button" onClick={() => setSelected(new Set())} className="text-xs text-gray-500">
            Clear
          </button>
        </div>
      )}
      {bulkResult && (
        <p className={`text-sm ${bulkPaused ? "text-amber-800" : "text-green-600"}`}>{bulkResult}</p>
      )}

      {resendConfirm && resendConfirm.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Already Sent</h3>
            <p className="mt-1 text-sm text-gray-600">
              The following employees have already received a welcome email:
            </p>
            <ul className="mt-3 max-h-48 space-y-2 overflow-y-auto text-sm">
              {resendConfirm.map((e) => (
                <li key={e.employee_id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                  <div>
                    <span className="font-medium text-gray-900">{e.name}</span>
                    <span className="ml-2 text-gray-500">{e.email}</span>
                  </div>
                  {e.sent_at && (
                    <span className="text-xs text-gray-400">
                      {new Date(e.sent_at).toLocaleDateString()}
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
                onClick={() => {
                  setResendConfirm(null);
                  setSelected(new Set());
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={bulkSending}
                className="rounded-lg bg-pme-blue px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                onClick={async () => {
                  setBulkSending(true);
                  try {
                    const ids = resendConfirm.map((e) => e.employee_id);
                    const res = await api.post<SendWelcomeResponse>("/api/employees/send-welcome-emails", {
                      employee_ids: ids,
                      force: true,
                    });
                    setBulkPaused(!!res.email_paused);
                    setBulkResult(res.email_paused ? WELCOME_PAUSED_NOTE : `Resend: ${res.sent} sent, ${res.failed} failed`);
                    const wStatus = await api.get<WelcomeStatus[]>("/api/employees/welcome-status");
                    const wMap: Record<number, WelcomeStatus> = {};
                    for (const w of wStatus) wMap[w.employee_id] = w;
                    setWelcomeMap(wMap);
                  } catch {
                    setBulkResult("Failed to resend");
                  } finally {
                    setBulkSending(false);
                    setResendConfirm(null);
                    setSelected(new Set());
                  }
                }}
              >
                {bulkSending ? "Sending..." : `Resend (${resendConfirm.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      {/* ── Mobile card layout ─────────────────────────────────── */}
      <div className="space-y-2 md:hidden">
        {filtered.map((r) => (
          <Link
            key={r.id}
            to={`/admin/employees/${r.id}`}
            className="block rounded-xl border border-gray-200 bg-white p-4 shadow-sm active:bg-gray-50"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900">
                  <span
                    aria-hidden
                    className={[
                      "mr-2 inline-block h-2 w-2 rounded-full align-middle",
                      !r.is_active
                        ? "bg-red-500"
                        : r.last_login_at
                          ? "bg-green-500"
                          : "bg-amber-400",
                    ].join(" ")}
                  />
                  {r.first_name} {r.last_name}
                </p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {r.employee_number} · {siteName(r.site_id)}
                </p>
                <p className="text-xs text-gray-500">{r.email}</p>
              </div>
              <span
                className={[
                  "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                  r.is_active
                    ? "bg-green-50 text-green-700"
                    : "bg-gray-100 text-gray-500",
                ].join(" ")}
              >
                {r.is_active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500">
              <span>{formatRole(r.role)}</span>
              {r.manager_name && <span>→ {r.manager_name}</span>}
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <p className="py-6 text-center text-sm text-gray-400">
            No employees match the current filters.
          </p>
        )}
      </div>

      {/* ── Desktop table ─────────────────────────────────────── */}
      <div className="hidden overflow-x-auto rounded-xl border border-gray-200 bg-white md:block">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="w-8 px-2 py-3">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelected(new Set(filtered.map((r) => r.id)));
                    } else {
                      setSelected(new Set());
                    }
                  }}
                />
              </th>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="hidden px-4 py-3 lg:table-cell">Site</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Invite</th>
              <th className="hidden px-4 py-3 lg:table-cell">Line Manager</th>
              <th className="px-4 py-3">Status</th>
              <th className="hidden px-4 py-3 lg:table-cell">Last login</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map((r) => {
              const ws = welcomeMap[r.id];
              return (
              <tr key={r.id}>
                <td className="w-8 px-2 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(r.id); else next.delete(r.id);
                      setSelected(next);
                    }}
                  />
                </td>
                <td className="px-4 py-3 font-mono text-xs text-gray-500">
                  {r.employee_number}
                </td>
                <td className="px-4 py-3 font-medium text-gray-900">
                  <span
                    aria-hidden
                    title={
                      !r.is_active
                        ? "Deactivated"
                        : r.last_login_at
                          ? "Active"
                          : "Never logged in"
                    }
                    className={[
                      "mr-2 inline-block h-2 w-2 rounded-full align-middle",
                      !r.is_active
                        ? "bg-red-500"
                        : r.last_login_at
                          ? "bg-green-500"
                          : "bg-amber-400",
                    ].join(" ")}
                  />
                  <Link to={`/admin/employees/${r.id}`} className="hover:underline">
                    {r.first_name} {r.last_name}
                  </Link>
                </td>
                <td className="px-4 py-3 text-gray-600">{r.email}</td>
                <td className="hidden px-4 py-3 lg:table-cell">{siteName(r.site_id)}</td>
                <td className="px-4 py-3">{formatRole(r.role)}</td>
                <td className="px-4 py-3 text-center">
                  {ws?.has_logged_in ? (
                    <span title="Logged in" className="inline-flex text-pme-blue"><Check size={16} strokeWidth={2} aria-label="Logged in" /></span>
                  ) : ws?.welcome_sent ? (
                    <span title={`Sent ${ws.welcome_sent_at ? new Date(ws.welcome_sent_at).toLocaleDateString() : ""}`} className="inline-flex text-green-600"><Mail size={16} strokeWidth={1.75} aria-label="Welcome email sent" /></span>
                  ) : (
                    <span title="Not sent" className="inline-flex text-gray-300"><Mail size={16} strokeWidth={1.75} aria-label="Not sent" /></span>
                  )}
                </td>
                <td className="hidden px-4 py-3 text-gray-600 lg:table-cell">
                  {r.manager_name ?? "—"}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={[
                      "rounded-full px-2 py-0.5 text-xs font-semibold",
                      r.is_active
                        ? "bg-green-50 text-green-700"
                        : "bg-gray-100 text-gray-500",
                    ].join(" ")}
                  >
                    {r.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="hidden px-4 py-3 text-gray-500 lg:table-cell">
                  {r.last_login_at ? TIME_FMT.format(new Date(r.last_login_at)) : "—"}
                </td>
              </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-6 text-center text-gray-400">
                  No employees match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
